import React, { useState, useEffect, useCallback } from 'react';
import styles from './ReportsModule.module.css';
import { Project } from '../../types';
import { reportsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface ReportsModuleProps {
  project: Project;
}

interface ReportItem {
  _id: string;
  title: string;
  type: string;
  format: string;
  status: 'generating' | 'completed' | 'failed' | 'scheduled';
  sections: any[];
  parameters?: {
    dateRange?: { start: string; end: string };
    audience?: string;
  };
  generatedContent?: {
    summary?: string;
    highlights?: string[];
    recommendations?: string[];
  };
  schedule?: {
    enabled: boolean;
    frequency?: string;
    nextRunAt?: string;
  };
  createdAt: string;
  createdBy?: { name: string };
}

interface AvailableType {
  type: string;
  label: string;
  description: string;
  dataAvailable: boolean;
  dataCount?: number;
  sections: string[];
}

const TYPE_LABELS: Record<string, string> = {
  project_status: 'Projectstatus',
  sentiment: 'Sentiment',
  stakeholder: 'Stakeholder',
  participation: 'Participatie',
  comprehensive: 'Integraal',
  custom: 'Aangepast'
};

const STATUS_LABELS: Record<string, string> = {
  generating: 'Genereren...',
  completed: 'Voltooid',
  failed: 'Mislukt',
  scheduled: 'Gepland'
};

const AUDIENCE_LABELS: Record<string, string> = {
  internal: 'Intern team',
  management: 'Management',
  stakeholders: 'Stakeholders',
  public: 'Publiek'
};

const ReportTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const props = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (type) {
    case 'comprehensive': return <svg {...props}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 18V12"/><path d="M12 18V8"/><path d="M16 18v-6"/></svg>;
    case 'sentiment': return <svg {...props}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
    case 'stakeholder': return <svg {...props}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
    default: return <svg {...props}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>;
  }
};

const SENTIMENT_LABELS: Record<string, string> = {
  very_positive: 'Zeer positief',
  positive: 'Positief',
  neutral: 'Neutraal',
  negative: 'Negatief',
  very_negative: 'Zeer negatief'
};

const SENTIMENT_COLORS: Record<string, string> = {
  very_positive: '#16a34a',
  positive: '#4ade80',
  neutral: '#94a3b8',
  negative: '#f97316',
  very_negative: '#dc2626'
};

const STAKEHOLDER_SENTIMENT_LABELS: Record<string, string> = {
  very_positive: 'Zeer positief',
  positive: 'Positief',
  neutral: 'Neutraal',
  negative: 'Negatief',
  very_negative: 'Zeer negatief',
  stable: 'Stabiel',
  improving: 'Verbeterend',
  declining: 'Dalend'
};

const SectionRenderer: React.FC<{ type: string; data: any }> = ({ type, data }) => {
  switch (type) {
    case 'summary':
      return (
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Project</span>
            <span className={styles.summaryValue}>{data.projectName || '-'}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Status</span>
            <span className={styles.summaryValue}>
              <span className={styles.statusDot} style={{ background: data.projectStatus === 'active' ? '#16a34a' : '#94a3b8' }} />
              {data.projectStatus || '-'}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Gegenereerd op</span>
            <span className={styles.summaryValue}>{data.generatedAt ? new Date(data.generatedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
          </div>
          {data.period?.start && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Periode</span>
              <span className={styles.summaryValue}>
                {new Date(data.period.start).toLocaleDateString('nl-NL')} — {new Date(data.period.end).toLocaleDateString('nl-NL')}
              </span>
            </div>
          )}
        </div>
      );

    case 'sentiment_analysis':
      return (
        <div>
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{data.total}</span>
              <span className={styles.statLabel}>Totaal items</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{data.averageScore}</span>
              <span className={styles.statLabel}>Gemiddelde score</span>
            </div>
          </div>
          {data.distribution && (
            <div className={styles.distributionChart}>
              <h4 className={styles.chartTitle}>Verdeling</h4>
              {Object.entries(data.distribution).map(([key, count]) => {
                const total = Object.values(data.distribution).reduce((s: number, v: any) => s + (v as number), 0) as number;
                const pct = total > 0 ? ((count as number) / total * 100) : 0;
                return (
                  <div key={key} className={styles.barRow}>
                    <span className={styles.barLabel}>{SENTIMENT_LABELS[key] || key}</span>
                    <div className={styles.barTrack}>
                      <div className={styles.barFill} style={{ width: `${pct}%`, background: SENTIMENT_COLORS[key] || '#94a3b8' }} />
                    </div>
                    <span className={styles.barCount}>{count as number}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );

    case 'stakeholder_overview':
      return (
        <div>
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{data.total}</span>
              <span className={styles.statLabel}>Totaal stakeholders</span>
            </div>
            {data.byType && Object.entries(data.byType).map(([key, count]) => (
              <div key={key} className={styles.statCard}>
                <span className={styles.statNumber}>{count as number}</span>
                <span className={styles.statLabel} style={{ textTransform: 'capitalize' }}>{key === 'individual' ? 'Individuen' : key === 'organization' ? 'Organisaties' : key}</span>
              </div>
            ))}
          </div>
          {data.relationships && data.relationships.length > 0 && (
            <div className={styles.stakeholderTable}>
              <div className={styles.tableHeader}>
                <span>Naam</span>
                <span>Type</span>
                <span>Sentiment</span>
                <span>Trend</span>
              </div>
              {data.relationships.map((r: any, idx: number) => (
                <div key={idx} className={styles.tableRow}>
                  <span className={styles.stakeholderName}>{r.name}</span>
                  <span className={styles.stakeholderType}>{r.type === 'individual' ? 'Individu' : r.type === 'organization' ? 'Organisatie' : r.type}</span>
                  <span>
                    <span className={styles.sentimentBadge} style={{ background: SENTIMENT_COLORS[r.sentiment?.current] || '#f1f5f9', color: SENTIMENT_COLORS[r.sentiment?.current] ? '#fff' : '#475569' }}>
                      {STAKEHOLDER_SENTIMENT_LABELS[r.sentiment?.current] || r.sentiment?.current || '-'}
                    </span>
                  </span>
                  <span className={styles.trendLabel}>
                    {r.sentiment?.trend === 'improving' ? '↑' : r.sentiment?.trend === 'declining' ? '↓' : '→'} {STAKEHOLDER_SENTIMENT_LABELS[r.sentiment?.trend] || r.sentiment?.trend || '-'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      );

    case 'werkplanning_status':
      return (
        <div>
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{data.total}</span>
              <span className={styles.statLabel}>Totaal taken</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber} style={{ color: '#16a34a' }}>{data.completed}</span>
              <span className={styles.statLabel}>Voltooid</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber} style={{ color: '#0284c7' }}>{data.inProgress}</span>
              <span className={styles.statLabel}>In uitvoering</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber} style={{ color: '#94a3b8' }}>{data.pending}</span>
              <span className={styles.statLabel}>Openstaand</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber} style={{ color: '#dc2626' }}>{data.overdue}</span>
              <span className={styles.statLabel}>Verlopen</span>
            </div>
          </div>
          {data.total > 0 && (
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                {data.completed > 0 && <div className={styles.progressSegment} style={{ width: `${(data.completed / data.total) * 100}%`, background: '#16a34a' }} />}
                {data.inProgress > 0 && <div className={styles.progressSegment} style={{ width: `${(data.inProgress / data.total) * 100}%`, background: '#0284c7' }} />}
                {data.pending > 0 && <div className={styles.progressSegment} style={{ width: `${(data.pending / data.total) * 100}%`, background: '#e2e8f0' }} />}
              </div>
              <span className={styles.progressText}>{data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0}% voltooid</span>
            </div>
          )}
        </div>
      );

    case 'strategy_progress':
      return (
        <div>
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{data.total}</span>
              <span className={styles.statLabel}>Totaal strategieën</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber} style={{ color: '#16a34a' }}>{data.active}</span>
              <span className={styles.statLabel}>Actief</span>
            </div>
          </div>
          {data.strategies && data.strategies.length > 0 && (
            <div className={styles.strategiesList}>
              {data.strategies.map((s: any, idx: number) => (
                <div key={idx} className={styles.strategyItem}>
                  <div className={styles.strategyName}>{s.name}</div>
                  <div className={styles.strategyMeta}>
                    <span className={`${styles.statusBadge} ${s.status === 'active' ? styles.status_completed : styles.status_scheduled}`}>
                      {s.status === 'active' ? 'Actief' : s.status === 'draft' ? 'Concept' : s.status}
                    </span>
                    <span className={styles.typeBadge}>{s.type}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );

    case 'recommendations':
      return (
        <div>
          {data.highlights && data.highlights.length > 0 && (
            <div className={styles.highlightsList}>
              {data.highlights.map((h: string, idx: number) => (
                <div key={idx} className={styles.highlightItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2z"/></svg>
                  {h}
                </div>
              ))}
            </div>
          )}
          {data.recommendations && data.recommendations.length > 0 ? (
            <ul className={styles.recommendationsList}>
              {data.recommendations.map((r: string, idx: number) => (
                <li key={idx}>{r}</li>
              ))}
            </ul>
          ) : (
            <p className={styles.noData}>Geen aanbevelingen beschikbaar voor deze rapportperiode.</p>
          )}
        </div>
      );

    default:
      return (
        <div className={styles.sectionData}>
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className={styles.dataRow}>
              <span className={styles.dataKey}>{key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}</span>
              <span className={styles.dataValue}>
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </span>
            </div>
          ))}
        </div>
      );
  }
};

export const ReportsModule: React.FC<ReportsModuleProps> = ({ project }) => {
  const toast = useToast();
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [availableTypes, setAvailableTypes] = useState<AvailableType[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'generate' | 'detail'>('list');
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);

  // Generate form
  const [generateForm, setGenerateForm] = useState({
    title: '',
    type: 'comprehensive',
    format: 'pdf',
    audience: 'internal',
    dateRangeStart: '',
    dateRangeEnd: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [reportsData, types] = await Promise.all([
        reportsAPI.getByProject(project.id),
        reportsAPI.getAvailable(project.id)
      ]);
      setReports(reportsData.reports || []);
      setAvailableTypes(types || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    try {
      const data: any = {
        project: project.id,
        title: generateForm.title || `${TYPE_LABELS[generateForm.type]} - ${new Date().toLocaleDateString('nl-NL')}`,
        type: generateForm.type,
        format: generateForm.format,
        parameters: {
          audience: generateForm.audience,
          dateRange: generateForm.dateRangeStart ? {
            start: generateForm.dateRangeStart,
            end: generateForm.dateRangeEnd || new Date().toISOString()
          } : undefined
        }
      };

      await reportsAPI.generate(data);
      toast.success('Rapport wordt gegenereerd');
      setView('list');
      // Refresh after a delay to show the generated report
      setTimeout(fetchData, 2000);
      fetchData();
    } catch (error) {
      toast.error('Fout bij genereren rapport');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await reportsAPI.delete(id);
      toast.success('Rapport verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const viewDetail = async (report: ReportItem) => {
    try {
      const full = await reportsAPI.getById(report._id);
      setSelectedReport(full);
      setView('detail');
    } catch (error) {
      toast.error('Fout bij laden rapport');
    }
  };

  if (loading) {
    return <div className={styles.container}><div className={styles.loading}>Laden...</div></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Rapportage & Export</h1>
          <p className={styles.subtitle}>Genereer rapporten vanuit alle projectmodules</p>
        </div>
        {view === 'list' ? (
          <button className={styles.primaryButton} onClick={() => setView('generate')}>
            + Nieuw Rapport
          </button>
        ) : (
          <button className={styles.secondaryButton} onClick={() => { setView('list'); setSelectedReport(null); }}>
            Terug naar overzicht
          </button>
        )}
      </div>

      {view === 'list' && (
        <>
          {/* Quick generate cards */}
          <div className={styles.quickGenerate}>
            <h3 className={styles.sectionTitle}>Snel genereren</h3>
            <div className={styles.typeCards}>
              {availableTypes.map(t => (
                <div key={t.type} className={`${styles.typeCard} ${!t.dataAvailable ? styles.typeCardDisabled : ''}`}
                  onClick={() => {
                    if (t.dataAvailable) {
                      setGenerateForm(prev => ({ ...prev, type: t.type, title: t.label }));
                      setView('generate');
                    }
                  }}>
                  <div className={styles.typeIcon}>
                    <ReportTypeIcon type={t.type} />
                  </div>
                  <h4>{t.label}</h4>
                  <p>{t.description}</p>
                  {t.dataCount !== undefined && <span className={styles.dataCount}>{t.dataCount} items</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Reports list */}
          <h3 className={styles.sectionTitle}>Gegenereerde rapporten ({reports.length})</h3>
          {reports.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              <h3>Nog geen rapporten</h3>
              <p>Genereer een rapport om projectinformatie samen te vatten en te exporteren.</p>
            </div>
          ) : (
            <div className={styles.reportsList}>
              {reports.map(report => (
                <div key={report._id} className={styles.reportCard} onClick={() => viewDetail(report)}>
                  <div className={styles.reportHeader}>
                    <div>
                      <h4 className={styles.reportTitle}>{report.title}</h4>
                      <div className={styles.reportMeta}>
                        <span className={`${styles.statusBadge} ${styles[`status_${report.status}`]}`}>{STATUS_LABELS[report.status]}</span>
                        <span className={styles.typeBadge}>{TYPE_LABELS[report.type]}</span>
                        <span className={styles.formatBadge}>{report.format?.toUpperCase()}</span>
                      </div>
                    </div>
                    <div className={styles.reportActions}>
                      <button className={styles.deleteButton} onClick={e => { e.stopPropagation(); handleDelete(report._id); }}>Verwijderen</button>
                    </div>
                  </div>
                  {report.generatedContent?.summary && (
                    <p className={styles.reportSummary}>{report.generatedContent.summary}</p>
                  )}
                  <div className={styles.reportFooter}>
                    <span>{new Date(report.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    {report.createdBy?.name && <span>door {report.createdBy.name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === 'generate' && (
        <div className={styles.generateForm}>
          <h2>Rapport Genereren</h2>

          <div className={styles.formGroup}>
            <label>Titel</label>
            <input type="text" value={generateForm.title} onChange={e => setGenerateForm(prev => ({ ...prev, title: e.target.value }))} placeholder="Bijv. Maandrapport maart 2026" />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Type rapport</label>
              <select value={generateForm.type} onChange={e => setGenerateForm(prev => ({ ...prev, type: e.target.value }))}>
                {availableTypes.map(t => (
                  <option key={t.type} value={t.type} disabled={!t.dataAvailable}>{t.label}{!t.dataAvailable ? ' (geen data)' : ''}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Doelgroep</label>
              <select value={generateForm.audience} onChange={e => setGenerateForm(prev => ({ ...prev, audience: e.target.value }))}>
                {Object.entries(AUDIENCE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Startdatum (optioneel)</label>
              <input type="date" value={generateForm.dateRangeStart} onChange={e => setGenerateForm(prev => ({ ...prev, dateRangeStart: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Einddatum (optioneel)</label>
              <input type="date" value={generateForm.dateRangeEnd} onChange={e => setGenerateForm(prev => ({ ...prev, dateRangeEnd: e.target.value }))} />
            </div>
          </div>

          {/* Selected type info */}
          {availableTypes.find(t => t.type === generateForm.type) && (
            <div className={styles.typeInfo}>
              <h4>Secties in dit rapport:</h4>
              <div className={styles.sectionsList}>
                {availableTypes.find(t => t.type === generateForm.type)!.sections.map(s => (
                  <span key={s} className={styles.sectionTag}>{s.replace(/_/g, ' ')}</span>
                ))}
              </div>
            </div>
          )}

          <div className={styles.formActions}>
            <button className={styles.secondaryButton} onClick={() => setView('list')}>Annuleren</button>
            <button className={styles.primaryButton} onClick={handleGenerate}>Rapport genereren</button>
          </div>
        </div>
      )}

      {view === 'detail' && selectedReport && (
        <div className={styles.detailView}>
          <h2>{selectedReport.title}</h2>
          <div className={styles.detailMeta}>
            <span className={`${styles.statusBadge} ${styles[`status_${selectedReport.status}`]}`}>{STATUS_LABELS[selectedReport.status]}</span>
            <span>{new Date(selectedReport.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          </div>

          {selectedReport.generatedContent?.summary && (
            <div className={styles.summarySection}>
              <h3>Samenvatting</h3>
              <p>{selectedReport.generatedContent.summary}</p>
            </div>
          )}

          {selectedReport.sections?.map((section, idx) => (
            <div key={idx} className={styles.reportSection}>
              <h3>{section.title}</h3>
              {section.data && <SectionRenderer type={section.type} data={section.data} />}
            </div>
          ))}

          {selectedReport.generatedContent?.highlights && selectedReport.generatedContent.highlights.length > 0 && (
            <div className={styles.recommendationsSection}>
              <h3>Highlights</h3>
              <ul>
                {selectedReport.generatedContent.highlights.map((h: string, idx: number) => (
                  <li key={idx}>{h}</li>
                ))}
              </ul>
            </div>
          )}

          {selectedReport.generatedContent?.recommendations && selectedReport.generatedContent.recommendations.length > 0 && (
            <div className={styles.recommendationsSection}>
              <h3>Aanbevelingen</h3>
              <ul>
                {selectedReport.generatedContent.recommendations.map((rec, idx) => (
                  <li key={idx}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
